import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, AlertTriangle, List, BarChart3, Settings, 
  Users, User, ShoppingCart, MapPin, Shield, Smartphone, 
  LogOut, DollarSign, FileText, TrendingUp, CreditCard, Clock,
  Search, ChevronLeft, ChevronRight, Menu, X, Activity, CalendarCheck, Fuel,
  LineChart, Package, Megaphone, Award,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { SCREEN_PRIVILEGES, ROLES } from '../config/roles';
import { APP_VERSION, BUILD_DATE } from '../config/constants';
import { getDisplayName, getInitial } from '../utils/displayName';
import { ANDROID_APK_PUBLIC_PATH } from '../utils/routerBasename';
import { SearchProvider, useGlobalSearch } from '../context/SearchContext';
import { normalizeSearch } from '../utils/globalSearch';
import NotificationBell from './NotificationBell';

const PremiumLayoutInner = ({ children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
    const [isSidebarHidden, setIsSidebarHidden] = useState(false);
    const { user, hasAllPrivileges, hasAnyPrivilege, loading, logout } = useAuth();
    const { query, setQuery } = useGlobalSearch();
    const location = useLocation();
    const navigate = useNavigate();

    const screen = (key) => SCREEN_PRIVILEGES[key];

    const navItems = [
        { group: 'Overview', items: [
            { id: 'overview', label: screen('overview').label, icon: LayoutDashboard, path: screen('overview').path, privileges: screen('overview').required },
        ]},
        { group: 'Administration', items: [
            { id: 'users', label: screen('users').label, icon: Users, path: screen('users').path, privileges: screen('users').required },
            { id: 'roles', label: screen('roles').label, icon: Shield, path: screen('roles').path, privileges: screen('roles').required },
            { id: 'announcements', label: screen('announcements').label, icon: Megaphone, path: screen('announcements').path, privileges: screen('announcements').required },
            { id: 'settings', label: screen('settings').label, icon: Settings, path: screen('settings').path, privileges: screen('settings').required },
        ]},
        { group: 'Field Force', items: [
            { id: 'reps', label: screen('reps').label, icon: User, path: screen('reps').path, privileges: screen('reps').required },
            { id: 'device-activation', label: screen('devices').label, icon: Smartphone, path: screen('devices').path, privileges: screen('devices').required },
        ]},
        { group: 'Sales & Metrics', items: [
            { id: 'targets', label: screen('targets').label, icon: BarChart3, path: screen('targets').path, privileges: screen('targets').required },
            { id: 'sales', label: screen('sales').label, icon: LineChart, path: screen('sales').path, privileges: screen('sales').required },
            { id: 'customers', label: screen('customers').label, icon: MapPin, path: screen('customers').path, privileges: screen('customers').required },
            { id: 'orders', label: screen('orders').label, icon: ShoppingCart, path: screen('orders').path, privileges: screen('orders').required },
            { id: 'products', label: screen('products').label, icon: Package, path: screen('products').path, privileges: screen('products').required },
            { id: 'incentives', label: screen('incentives').label, icon: Award, path: screen('incentives').path, privileges: screen('incentives').required },
        ]},
        { group: 'Finances', items: [
            { id: 'salary-config', label: screen('salaryConfig').label, icon: DollarSign, path: screen('salaryConfig').path, privileges: screen('salaryConfig').required },
            { id: 'salary-processing', label: screen('salaryProcessing').label, icon: TrendingUp, path: screen('salaryProcessing').path, privileges: screen('salaryProcessing').required },
            { id: 'salary-slips', label: screen('salarySlips').label, icon: FileText, path: screen('salarySlips').path, privileges: screen('salarySlips').required },
            { id: 'payment-tracking', label: screen('payments').label, icon: CreditCard, path: screen('payments').path, privileges: screen('payments').required },
        ]},
        { group: 'Monitoring', items: [
            { id: 'attendance', label: screen('attendance').label, icon: Clock, path: screen('attendance').path, privileges: screen('attendance').required },
            { id: 'leave', label: screen('leave').label, icon: CalendarCheck, path: screen('leave').path, privileges: screen('leave').required },
            { id: 'logs', label: screen('logs').label, icon: List, path: screen('logs').path, privileges: screen('logs').required },
            { id: 'fuel', label: screen('fuel').label, icon: Fuel, path: screen('fuel').path, privileges: screen('fuel').required },
            { id: 'analytics', label: screen('analytics').label, icon: BarChart3, path: screen('analytics').path, privileges: screen('analytics').required },
            { id: 'alerts', label: screen('alerts').label, icon: AlertTriangle, path: screen('alerts').path, privileges: screen('alerts').required },
            { id: 'system-logs', label: screen('systemLogs').label, icon: Activity, path: screen('systemLogs').path, privileges: screen('systemLogs').required },
        ]}
    ];

    const filterItems = (items) => {
        let visible = items;
        if (user && !loading && String(user.role || '').toLowerCase() !== ROLES.ADMIN) {
            visible = visible.filter((item) => {
                if (item.anyPrivileges && item.anyPrivileges.length) {
                    return hasAnyPrivilege(item.anyPrivileges);
                }
                return hasAllPrivileges(item.privileges || []);
            });
        }
        const q = normalizeSearch(query);
        if (q) {
            visible = visible.filter(
                (item) =>
                    normalizeSearch(item.label).includes(q) ||
                    normalizeSearch(item.id).includes(q) ||
                    normalizeSearch(item.path).includes(q)
            );
        }
        return visible;
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-inter">
            {/* Mobile drawer backdrop */}
            <AnimatePresence>
                {isMobileDrawerOpen && (
                    <motion.button
                        type="button"
                        aria-label="Close navigation"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsMobileDrawerOpen(false)}
                        className="fixed inset-0 bg-black/40 z-40 md:hidden"
                    />
                )}
            </AnimatePresence>

            {/* Mobile drawer */}
            <AnimatePresence>
                {isMobileDrawerOpen && (
                    <motion.aside
                        initial={{ x: -320 }}
                        animate={{ x: 0 }}
                        exit={{ x: -320 }}
                        transition={{ type: 'tween', duration: 0.25 }}
                        className="fixed left-0 top-0 bottom-0 w-[280px] bg-[#0F172A] text-white z-50 shadow-2xl overflow-hidden flex flex-col md:hidden"
                    >
                        <div className="p-4 flex items-center justify-between border-b border-white/5 h-16">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                    <Shield className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-base font-bold tracking-tight">QUARRYFORCE</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsMobileDrawerOpen(false)}
                                className="p-2 rounded-lg hover:bg-white/5 text-slate-300"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar py-4 px-3 space-y-6">
                            {navItems.map((group, gIdx) => {
                                const visibleItems = filterItems(group.items);
                                if (visibleItems.length === 0) return null;
                                return (
                                    <div key={gIdx} className="space-y-1">
                                        <h3 className="px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-2">
                                            {group.group}
                                        </h3>
                                        {visibleItems.map((item) => {
                                            const Icon = item.icon;
                                            return (
                                                <NavLink
                                                    key={item.id}
                                                    to={item.path}
                                                    onClick={() => setIsMobileDrawerOpen(false)}
                                                    className={({ isActive }) => `
                                                        flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative
                                                        ${isActive 
                                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                                                            : 'text-slate-400 hover:text-white hover:bg-white/5'}
                                                    `}
                                                >
                                                    <Icon className="w-5 h-5 flex-shrink-0" />
                                                    <span className="font-medium text-sm whitespace-nowrap overflow-hidden">
                                                        {item.label}
                                                    </span>
                                                </NavLink>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>

            {/* Glass Sidebar */}
            <motion.aside 
                initial={false}
                animate={{ width: isSidebarHidden ? 0 : (isSidebarOpen ? 280 : 80) }}
                className="hidden md:flex bg-[#0F172A] text-white flex-shrink-0 z-30 relative shadow-2xl overflow-hidden flex-col"
            >
                {/* Decorative background glow */}
                <div className="absolute top-0 -left-20 w-40 h-40 bg-blue-500/10 blur-[100px]" />
                <div className="absolute bottom-0 -right-20 w-40 h-40 bg-indigo-500/10 blur-[100px]" />

                {/* Logo Area */}
                <div className="p-6 flex items-center justify-between border-b border-white/5 relative h-20">
                    <AnimatePresence mode="wait">
                        {isSidebarOpen ? (
                            <motion.div 
                                key="full-logo"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className="flex items-center gap-3"
                            >
                                <div className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                    <Shield className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/70">
                                    QUARRYFORCE
                                </span>
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="small-logo"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center mx-auto"
                            >
                                <Shield className="w-6 h-6 text-white" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto custom-scrollbar py-4 px-3 space-y-6">
                    {navItems.map((group, gIdx) => {
                        const visibleItems = filterItems(group.items);
                        if (visibleItems.length === 0) return null;

                        return (
                            <div key={gIdx} className="space-y-1">
                                {isSidebarOpen && (
                                    <h3 className="px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-2">
                                        {group.group}
                                    </h3>
                                )}
                                {visibleItems.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = location.pathname.startsWith(item.path);
                                    
                                    return (
                                        <NavLink
                                            key={item.id}
                                            to={item.path}
                                            className={({ isActive }) => `
                                                flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative
                                                ${isActive 
                                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                                                    : 'text-slate-400 hover:text-white hover:bg-white/5'}
                                            `}
                                        >
                                            <Icon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                                            {isSidebarOpen && (
                                                <span className="font-medium text-sm whitespace-nowrap overflow-hidden">
                                                    {item.label}
                                                </span>
                                            )}
                                            {isActive && (
                                                <motion.div 
                                                    layoutId="active-nav-glow"
                                                    className="absolute -left-3 w-1 h-8 bg-blue-400 rounded-r-full blur-[2px]"
                                                />
                                            )}
                                        </NavLink>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                {/* Bottom User Area */}
                <div className="p-4 border-t border-white/5 space-y-4">
                    <button 
                        onClick={() => {
                            if (isSidebarHidden) {
                                setIsSidebarHidden(false);
                                setIsSidebarOpen(true);
                                return;
                            }
                            setIsSidebarOpen(!isSidebarOpen);
                        }}
                        className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-white/5 text-slate-400 transition-colors"
                    >
                        {isSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    {isSidebarOpen && (
                        <p className="text-center text-[10px] text-slate-500 tracking-wide">
                            {APP_VERSION} · {BUILD_DATE}
                        </p>
                    )}
                </div>
            </motion.aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col h-full min-w-0 overflow-hidden bg-[#F8FAFC]">
                {/* Premium Header */}
                <header className="min-h-16 md:h-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex flex-wrap items-center justify-between px-3 sm:px-4 md:px-8 z-20 gap-2 sm:gap-3 py-2 md:py-0">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 basis-full sm:basis-auto order-1">
                        {/* Mobile hamburger */}
                        <button
                            type="button"
                            onClick={() => setIsMobileDrawerOpen(true)}
                            className="md:hidden p-2 rounded-xl hover:bg-slate-100 text-slate-700"
                            aria-label="Open navigation"
                        >
                            <Menu className="w-5 h-5" />
                        </button>

                        {/* Desktop menu show/hide */}
                        <button
                            type="button"
                            onClick={() => {
                                if (isSidebarHidden) {
                                    setIsSidebarHidden(false);
                                    setIsSidebarOpen(true);
                                } else {
                                    setIsSidebarHidden(true);
                                }
                            }}
                            className="hidden md:inline-flex p-2 rounded-xl hover:bg-slate-100 text-slate-700"
                            aria-label={isSidebarHidden ? 'Show menu' : 'Hide menu'}
                            title={isSidebarHidden ? 'Show menu' : 'Hide menu'}
                        >
                            <Menu className="w-5 h-5" />
                        </button>

                        <div className="relative max-w-md w-full group min-w-0 max-[480px]:hidden">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search reps, customers, orders..."
                                aria-label="Search dashboard"
                                className="w-full pl-10 pr-10 py-2.5 bg-slate-100 border border-transparent focus:bg-white focus:border-blue-200 rounded-xl text-sm transition-all outline-none"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => setQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-semibold"
                                    aria-label="Clear search"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4 shrink-0 order-2 ml-auto">
                        <div className="flex items-center gap-2">
                            <NotificationBell />
                        </div>

                        {/* Download App Action */}
                        <div className="hidden lg:flex items-center gap-2 pr-4 border-r border-slate-200">
                           <a 
                             href={ANDROID_APK_PUBLIC_PATH} 
                             download
                             className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl text-sm font-bold hover:bg-blue-100 transition-colors group"
                             title="Download Android APK"
                           >
                              <Smartphone className="w-4 h-4 group-hover:scale-110 transition-transform" />
                              <span>Android App</span>
                           </a>
                           {/* Placeholder for iOS */}
                           <button 
                             disabled
                             className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-slate-400 rounded-xl text-sm font-bold cursor-not-allowed"
                             title="App Store coming soon"
                           >
                              <Shield className="w-4 h-4" />
                              <span>iOS (Soon)</span>
                           </button>
                        </div>


                        {/* User Profile */}
                        {user && (
                            <div className="flex items-center gap-3 pl-3 sm:pl-6 border-l border-slate-200">
                                <div className="text-right hidden sm:block">
                                    <p className="text-sm font-bold text-slate-800">{getDisplayName(user)}</p>
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600">{user.role}</p>
                                </div>
                                <div className="relative group">
                                    <button className="w-10 h-10 rounded-xl bg-gradient-to-tr from-slate-200 to-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold overflow-hidden shadow-sm hover:shadow-md transition-all">
                                        {user.avatar ? <img src={user.avatar} alt="" /> : getInitial(user)}
                                    </button>
                                    
                                    {/* Minimal Quick Action Dropdown Concept */}
                                    <div className="absolute right-0 top-full pt-2 opacity-0 scale-95 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto transition-all duration-200 z-50">
                                        <div className="w-48 bg-white rounded-2xl shadow-2xl border border-slate-100 p-2">
                                            <button 
                                                onClick={handleLogout}
                                                className="w-full flex items-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl text-sm font-semibold transition-colors"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Log out
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </header>

                {/* Viewport Content */}
                <div className="flex-1 overflow-y-auto overflow-x-auto p-3 sm:p-8 relative custom-scrollbar min-w-0 max-w-full">
                    {/* Background Decorative Blob */}
                    <div className="absolute top-0 right-0 -z-10 w-full h-full overflow-hidden pointer-events-none opacity-50">
                        <div className="absolute -top-[10%] -right-[10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full" />
                        <div className="absolute top-[40%] -left-[10%] w-[30%] h-[30%] bg-indigo-500/5 blur-[100px] rounded-full" />
                    </div>

                    <motion.div 
                        key={location.pathname}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="w-full max-w-full min-w-0 min-h-full"
                    >
                        {children}
                    </motion.div>
                </div>
            </main>
        </div>
    );
};

const PremiumLayout = ({ children }) => (
    <SearchProvider>
        <PremiumLayoutInner>{children}</PremiumLayoutInner>
    </SearchProvider>
);

export default PremiumLayout;
